import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, InputGroup, Row, Col } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { REGISTER } from '../graphql/operations';
import Footer from '../components/Footer';

interface RegisterPageProps {
  onRegisterSuccess: () => void;
}

const RegisterPage: React.FC<RegisterPageProps> = ({ onRegisterSuccess }) => {
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state && (location.state as any).from === '/order') {
      setInfoMessage('Please register to complete your checkout.');
    }
  }, [location]);

  // Redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/');
    }
  }, [navigate]);

  const [registerMutation, { loading }] = useMutation(REGISTER, {
    onError: (err) => {
      setErrorMessage(err.message || 'Registration failed. Please try again.');
    },
    onCompleted: (data) => {
      if (data?.register?.token) {
        localStorage.setItem('token', data.register.token);
        localStorage.setItem('username', data.register.user.username);
        localStorage.setItem('role', data.register.user.role);
        localStorage.setItem('companyName', data.register.user.companyName || '');
        localStorage.setItem('gstNumber', data.register.user.gstNumber || '');
        localStorage.setItem('companyAddress', data.register.user.companyAddress || '');
        localStorage.setItem('shippingAddress', data.register.user.shippingAddress || '');
        setSuccessMessage('Registration successful! Logging you in...');
        onRegisterSuccess();
        setTimeout(() => {
          const redirectPath = (location.state as any)?.from || '/';
          const redirectState = (location.state as any)?.fromState || null;
          navigate(redirectPath, { state: redirectState });
        }, 1500);
      } else {
        setErrorMessage('Failed to receive authorization token.');
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    
    const userTrimmed = username.trim();
    if (!userTrimmed || !password || !confirmPassword) {
      setErrorMessage('Please fill in all fields.');
      return;
    }

    if (userTrimmed.length < 3) {
      setErrorMessage('Username must be at least 3 characters long.');
      return;
    }

    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    try {
      await registerMutation({
        variables: {
          username: userTrimmed,
          password: password,
          companyName: companyName.trim(),
          gstNumber: gstNumber.trim(),
          companyAddress: companyAddress.trim(),
          shippingAddress: shippingAddress.trim(),
        },
      });
    } catch (err) {
      // Handled by onError callback
    }
  };

  return (
    <div className="page-fade d-flex flex-column register-page-wrapper" style={{ minHeight: 'calc(100vh - 68px)', backgroundColor: 'var(--bg-secondary)' }}>
      <div className="d-flex align-items-center justify-content-center flex-grow-1 py-5" style={{ background: 'linear-gradient(135deg, rgba(233, 30, 140, 0.03) 0%, rgba(59, 91, 219, 0.03) 100%)' }}>
        <Container style={{ maxWidth: '560px' }}>
          <Card className="border-0 shadow-lg p-3 bg-white rounded-4" style={{ overflow: 'hidden', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.8)' }}>
            <div className="text-center pt-4 pb-3">
              <div className="d-inline-flex align-items-center justify-content-center bg-light rounded-3 p-3 mb-3 border" style={{ width: '60px', height: '60px', color: 'var(--pink)' }}>
                <i className="fas fa-user-plus" style={{ fontSize: '24px' }}></i>
              </div>
              <h3 className="fw-bold text-dark mb-1">Create Account</h3>
              <p className="text-muted" style={{ fontSize: '13px' }}>Join Prathitha Digital Printers</p>
            </div>

            <Card.Body className="px-4 pb-4 pt-2">
              {errorMessage && (
                <Alert variant="danger" className="border-0 py-2 rounded-3 mb-4 d-flex align-items-center gap-2" style={{ fontSize: '12px' }}>
                  <i className="fas fa-exclamation-triangle"></i>
                  <div>{errorMessage}</div>
                </Alert>
              )}

              {infoMessage && (
                <Alert variant="info" className="border-0 py-2.5 rounded-3 mb-4 d-flex align-items-center gap-2" style={{ fontSize: '12.5px', backgroundColor: '#eef2ff', color: '#3b5bdb' }}>
                  <i className="fas fa-info-circle"></i>
                  <div>{infoMessage}</div>
                </Alert>
              )}

              {successMessage && (
                <Alert variant="success" className="border-0 py-2 rounded-3 mb-4 d-flex align-items-center gap-2" style={{ fontSize: '12px' }}>
                  <i className="fas fa-check-circle"></i>
                  <div>{successMessage}</div>
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row className="g-3 mb-4 text-start">
                  <Col md={12}>
                    <Form.Group controlId="formUsername">
                      <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12.5px' }}>Username*</Form.Label>
                      <InputGroup className="border rounded-3 overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                        <InputGroup.Text className="bg-white border-0 text-muted px-3">
                          <i className="far fa-user"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          className="border-0 py-2 form-input-login"
                          placeholder="Choose a username"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          style={{ fontSize: '14px', outline: 'none', boxShadow: 'none' }}
                          autoComplete="username"
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="formPassword">
                      <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12.5px' }}>Password*</Form.Label>
                      <InputGroup className="border rounded-3 overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                        <InputGroup.Text className="bg-white border-0 text-muted px-3">
                          <i className="fas fa-key"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          className="border-0 py-2 form-input-login"
                          placeholder="Min 6 chars"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          style={{ fontSize: '14px', outline: 'none', boxShadow: 'none' }}
                          autoComplete="new-password"
                        />
                        <Button 
                          variant="link" 
                          className="bg-white border-0 text-muted px-3"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ boxShadow: 'none' }}
                        >
                          <i className={showPassword ? 'far fa-eye-slash' : 'far fa-eye'}></i>
                        </Button>
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="formConfirmPassword">
                      <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12.5px' }}>Confirm Password*</Form.Label>
                      <InputGroup className="border rounded-3 overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                        <InputGroup.Text className="bg-white border-0 text-muted px-3">
                          <i className="fas fa-key"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          className="border-0 py-2 form-input-login"
                          placeholder="Confirm password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          style={{ fontSize: '14px', outline: 'none', boxShadow: 'none' }}
                          autoComplete="new-password"
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="formCompanyName">
                      <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12.5px' }}>Company Name <span className="text-muted fw-normal">(Optional)</span></Form.Label>
                      <InputGroup className="border rounded-3 overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                        <InputGroup.Text className="bg-white border-0 text-muted px-3">
                          <i className="fas fa-building"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          className="border-0 py-2 form-input-login"
                          placeholder="e.g. Acme Corp"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          style={{ fontSize: '14px', outline: 'none', boxShadow: 'none' }}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="formGstNumber">
                      <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12.5px' }}>GST Number <span className="text-muted fw-normal">(Optional)</span></Form.Label>
                      <InputGroup className="border rounded-3 overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                        <InputGroup.Text className="bg-white border-0 text-muted px-3">
                          <i className="fas fa-percent"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          className="border-0 py-2 form-input-login"
                          placeholder="15-digit GSTIN"
                          value={gstNumber}
                          onChange={(e) => setGstNumber(e.target.value)}
                          style={{ fontSize: '14px', outline: 'none', boxShadow: 'none' }}
                          maxLength={15}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group controlId="formCompanyAddress">
                      <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12.5px' }}>Company Address <span className="text-muted fw-normal">(Optional)</span></Form.Label>
                      <InputGroup className="border rounded-3 overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                        <InputGroup.Text className="bg-white border-0 text-muted px-3">
                          <i className="fas fa-map-marked-alt"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          className="border-0 py-2 form-input-login"
                          placeholder="Registered address"
                          value={companyAddress}
                          onChange={(e) => setCompanyAddress(e.target.value)}
                          style={{ fontSize: '14px', outline: 'none', boxShadow: 'none' }}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group controlId="formShippingAddress">
                      <Form.Label className="fw-semibold text-dark mb-1" style={{ fontSize: '12.5px' }}>Shipping / Delivery Address <span className="text-muted fw-normal">(Optional)</span></Form.Label>
                      <InputGroup className="border rounded-3 overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                        <InputGroup.Text className="bg-white border-0 text-muted px-3">
                          <i className="fas fa-truck"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          className="border-0 py-2 form-input-login"
                          placeholder="Delivery address"
                          value={shippingAddress}
                          onChange={(e) => setShippingAddress(e.target.value)}
                          style={{ fontSize: '14px', outline: 'none', boxShadow: 'none' }}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-100 py-2.5 fw-semibold text-white d-flex align-items-center justify-content-center gap-2 mb-3"
                  style={{
                    background: 'linear-gradient(90deg, #e91e8c 0%, #3b5bdb 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                  onMouseOut={(e) => (e.currentTarget.style.transform = 'none')}
                >
                  {loading ? (
                    <><i className="fas fa-spinner fa-spin me-1"></i> Registering...</>
                  ) : (
                    <>Sign Up <i className="fas fa-user-plus" style={{ fontSize: '11px' }}></i></>
                  )}
                </Button>

                <div className="text-center mt-3">
                  <span className="text-muted small">Already have an account? </span>
                  <Link to="/login" state={location.state} className="small fw-semibold text-decoration-none" style={{ color: 'var(--pink)' }}>Sign In</Link>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />
    </div>
  );
};

export default RegisterPage;
